import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function WelcomeEmail() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [welcomeWellisId, setWelcomeWellisId] = useState('');
  const [welcomePassword, setWelcomePassword] = useState('');
  const [sendingWelcomeEmail, setSendingWelcomeEmail] = useState(false);
  const [welcomeEmailMessage, setWelcomeEmailMessage] = useState('');
  const [welcomeEmailError, setWelcomeEmailError] = useState('');

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState('');

  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  // Search for users
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Change user email (WELLIS-BJOECHIA only)
  const [editingEmailUserId, setEditingEmailUserId] = useState(null);
  const [newEmailValue, setNewEmailValue] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  const [changeEmailMessage, setChangeEmailMessage] = useState('');
  const [changeEmailError, setChangeEmailError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/welcome-email-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load welcome email log');
      }
      setLogs(data.logs || []);
    } catch (err) {
      setLogsError(err.message || 'Failed to load welcome email log');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    fetchLogs();
  }, [user, fetchLogs]);

  // Debounced search-as-you-type, same pattern as /balances' member search.
  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchError('');
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/welcome-email/search-users?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Search failed');
        }
        setSearchResults(data.members || []);
      } catch (err) {
        setSearchError(err.message || 'Search failed');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, user]);

  const handleStartEditEmail = (member) => {
    setEditingEmailUserId(member.id);
    setNewEmailValue(member.email || '');
    setChangeEmailMessage('');
    setChangeEmailError('');
  };

  const handleCancelEditEmail = () => {
    setEditingEmailUserId(null);
    setNewEmailValue('');
  };

  const handleSaveEmail = async (member) => {
    setChangeEmailMessage('');
    setChangeEmailError('');
    if (!newEmailValue.trim()) {
      setChangeEmailError('Email is required');
      return;
    }
    setChangingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/change-user-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: member.id, new_email: newEmailValue.trim() }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to update email');
      }
      setChangeEmailMessage(data.message || 'Email updated');
      setSearchResults((prev) => prev.map((m) => (m.id === member.id ? { ...m, email: data.email } : m)));
      setEditingEmailUserId(null);
      setNewEmailValue('');
    } catch (err) {
      setChangeEmailError(err.message || 'Failed to update email');
    } finally {
      setChangingEmail(false);
    }
  };

  const handleSendWelcomeEmail = async (e) => {
    e.preventDefault();
    setWelcomeEmailMessage('');
    setWelcomeEmailError('');
    if (!welcomeWellisId.trim() || !welcomePassword.trim()) {
      setWelcomeEmailError('Wellis ID and password are both required');
      return;
    }
    setSendingWelcomeEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/send-welcome-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wellis_id: welcomeWellisId.trim(), password: welcomePassword.trim() }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to send welcome email');
      }
      setWelcomeEmailMessage(data.message || 'Welcome email sent');
      setWelcomeWellisId('');
      setWelcomePassword('');
      await fetchLogs();
    } catch (err) {
      setWelcomeEmailError(err.message || 'Failed to send welcome email');
    } finally {
      setSendingWelcomeEmail(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    // app_wellis.email_logs.attempted_at is a naive TIMESTAMP (no tz
    // marker) storing Postgres's NOW(), which is UTC on this server --
    // PostgREST returns it without a "Z" suffix, so new Date(dateString)
    // would otherwise be parsed as browser-local time instead of UTC,
    // silently shifting it before the Asia/Singapore conversion below is
    // even applied. Force UTC parsing by appending "Z" if it's missing.
    const hasTimezone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(dateString);
    const utcDateString = hasTimezone ? dateString : `${dateString}Z`;
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Singapore', hour12: false,
    }).format(new Date(utcDateString));
  };

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto">
        <div className="mb-8 border-b pb-6" style={{ borderColor: '#c9a961' }}>
          <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>Welcome Email</h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            Send the bilingual (EN/ZH) welcome email to a newly processed member, and review what's already been sent.
          </p>
        </div>

        {/* Send Welcome Email */}
        <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-3" style={{ color: '#c9a961' }}>Send Welcome Email</h2>
          <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
            Key in a member's Wellis ID and the password you set for them. Sends the bilingual (EN/ZH) welcome email with these login details.
          </p>
          {welcomeEmailMessage && (
            <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{welcomeEmailMessage}</p>
          )}
          {welcomeEmailError && (
            <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{welcomeEmailError}</p>
          )}
          <form onSubmit={handleSendWelcomeEmail} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Login ID (Wellis ID)</label>
              <input
                type="text"
                value={welcomeWellisId}
                onChange={(e) => setWelcomeWellisId(e.target.value)}
                placeholder="WELLIS-XXXXXXXXXX"
                className="p-2 rounded border text-body-sm"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>
            <div>
              <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Password</label>
              <input
                type="text"
                value={welcomePassword}
                onChange={(e) => setWelcomePassword(e.target.value)}
                className="p-2 rounded border text-body-sm"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>
            <button
              type="submit"
              disabled={sendingWelcomeEmail}
              className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {sendingWelcomeEmail ? 'Sending...' : 'Send Welcome Email'}
            </button>
          </form>
        </div>

        {/* Search Users */}
        <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-3" style={{ color: '#c9a961' }}>Search Users</h2>
          <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
            Search by Wellis ID, name, or email.{isBjoechia ? ' As WELLIS-BJOECHIA, you can also correct a member\'s email address here.' : ''}
          </p>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full p-2 rounded border text-body-sm mb-3"
            style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
          />

          {changeEmailMessage && (
            <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{changeEmailMessage}</p>
          )}
          {changeEmailError && (
            <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{changeEmailError}</p>
          )}
          {searchError && (
            <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{searchError}</p>
          )}

          {searching ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>Searching...</p>
          ) : searchQuery.trim().length >= 2 && searchResults.length === 0 ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>No matches.</p>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {searchResults.map((member) => (
                <div key={member.id} className="border rounded p-3" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-mono text-body-sm" style={{ color: '#c9a961' }}>{member.affiliate_code}</p>
                      <p className="text-body-xs" style={{ color: '#b8a68f' }}>
                        {member.first_name} {member.last_name} · {member.role} · {member.status}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setWelcomeWellisId(member.affiliate_code || '')}
                      className="px-3 py-1 text-xs rounded font-medium"
                      style={{ backgroundColor: '#3d5a4a', color: '#c9a961' }}
                    >
                      Use for Welcome Email
                    </button>
                  </div>

                  {editingEmailUserId === member.id ? (
                    <div className="flex gap-2 items-center mt-2">
                      <input
                        type="email"
                        value={newEmailValue}
                        onChange={(e) => setNewEmailValue(e.target.value)}
                        disabled={changingEmail}
                        className="flex-1 p-2 rounded border text-body-xs"
                        style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                      />
                      <button
                        type="button"
                        onClick={() => handleSaveEmail(member)}
                        disabled={changingEmail}
                        className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50 text-black"
                        style={{ backgroundColor: '#c9a961' }}
                      >
                        {changingEmail ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEditEmail}
                        disabled={changingEmail}
                        className="px-3 py-1 text-xs rounded font-medium border disabled:opacity-50"
                        style={{ borderColor: '#3d5a4a', color: '#b8a68f' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-body-xs" style={{ color: '#b8a68f' }}>{member.email || '— no email —'}</p>
                      {isBjoechia && (
                        <button
                          type="button"
                          onClick={() => handleStartEditEmail(member)}
                          className="px-2 py-0.5 text-xs rounded font-medium"
                          style={{ backgroundColor: 'transparent', color: '#c9a961', border: '1px solid #c9a961' }}
                        >
                          Change Email
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Sent log */}
        <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>Sent Welcome Emails</h2>
            <button
              onClick={fetchLogs}
              disabled={logsLoading}
              className="px-3 py-1 text-xs rounded font-medium"
              style={{ backgroundColor: '#c9a961', color: '#000' }}
            >
              {logsLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {logsError && (
            <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{logsError}</p>
          )}

          {logsLoading ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>Loading...</p>
          ) : logs.length === 0 ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>No welcome emails sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(201,169,97,0.3)' }}>
                    <th className="text-left py-2 px-2" style={{ color: '#b8a68f' }}>Wellis ID</th>
                    <th className="text-left py-2 px-2" style={{ color: '#b8a68f' }}>Name</th>
                    <th className="text-left py-2 px-2" style={{ color: '#b8a68f' }}>Email</th>
                    <th className="text-left py-2 px-2" style={{ color: '#b8a68f' }}>Status</th>
                    <th className="text-left py-2 px-2" style={{ color: '#b8a68f' }}>Sent At (SGT)</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid rgba(201,169,97,0.15)' }}>
                      <td className="py-2 px-2 font-mono" style={{ color: '#c9a961' }}>{log.wellis_id || '—'}</td>
                      <td className="py-2 px-2" style={{ color: '#b8a68f' }}>{log.recipient_name || '—'}</td>
                      <td className="py-2 px-2" style={{ color: '#b8a68f' }}>{log.email}</td>
                      <td className="py-2 px-2">
                        <span style={{ color: log.status === 'sent' ? '#4ade80' : '#e05252' }}>
                          {log.status === 'sent' ? '✓ Sent' : `✗ ${log.status}`}
                        </span>
                        {log.error && (
                          <span className="block text-body-xs mt-0.5" style={{ color: '#999999' }}>{log.error}</span>
                        )}
                      </td>
                      <td className="py-2 px-2" style={{ color: '#b8a68f' }}>{formatDate(log.attempted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
